import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { ShowsStore } from '../../../shows/core/stores/ShowsStore';
import { ChipComponent } from '../../../../shared/components/chip/chip.component';
import { CartStore } from '../../core/stores/CartStore';
import { WidthDirective } from '../../../../shared/directives/width.directive';
import { SwitchComponent } from '../../../../shared/components/switch/switch.component';
import { FormsModule } from '@angular/forms';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { CartService } from '../../core/services/CartService';
import { ICartItem } from '../../core/interfaces/ICartItem';
import { IShowPriceCategory } from '../../../shows/core/interfaces/IShowPriceCategory';
import { ArrayFilterPipe } from '../../../../shared/pipes/array-filter.pipe';
import { ArraySortByKeyPipe } from '../../../../shared/pipes/array-sort-by-key.pipe';
import { StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { ReservationsStore } from '../../../reservations/core/stores/ReservationsStore';
import { SellingOverviewPriceCategoryGateService } from '../../../shows/core/services/SellingOverviewPriceCategoryGateService';

import {
  TicketPackageSelectionModalComponent
} from '../../../kiosk/modals/ticket-package-selection-modal/ticket-package-selection-modal.component';
import { finalize, forkJoin, switchMap } from 'rxjs';
import { IShow } from '../../../shows/core/interfaces/IShow';
import { KioskStore } from '../../../kiosk/core/stores/KioskStore';
import { PackageUtils } from '../../../../core/utils/PackageUtils';
import { CardCodeInputModalComponent } from '../../../cards/modals/card-code-input-modal/card-code-input-modal.component';
import { PriceCategoryGildepassService } from '../../core/services/PriceCategoryGildepassService';
import {
  CinevillePartnerCodeModalComponent
} from '../cineville-partner-code-modal/cineville-partner-code-modal.component';
import { AlertModalComponent } from '../../../../shared/components/alert-modal/alert-modal.component';
import { CinevilleUtils } from '../../core/utils/CinevilleUtils';

@Component({
  selector: 'app-price-category-picker-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    ModalComponent,
    ChipComponent,
    WidthDirective,
    SwitchComponent,
    FormsModule,
    ArrayFilterPipe,
    ArraySortByKeyPipe,
    StepperComponent,
    CardCodeInputModalComponent,
    CinevillePartnerCodeModalComponent
  ],
  templateUrl: './price-category-picker-modal.component.html',
  styleUrl: './price-category-picker-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PriceCategoryPickerModalComponent extends BaseModalComponent<{ ticket: ICartItem; }> {

  @ViewChild('cardCodeInputModalComponent') cardCodeInputModalComponent: CardCodeInputModalComponent;
  @ViewChild(CinevillePartnerCodeModalComponent) private _cinevillePartnerCodeModal: CinevillePartnerCodeModalComponent;

  public selectedTicketsCount = 0;
  public selectedPriceCategoryId: number;
  public initialSelectedPriceCategoryId: number;
  public isApplyToAllEnabled = false;
  public show: IShow;
  public priceCategories: IShowPriceCategory[] = [];
  public showTickets: ICartItem[] = [];
  public readonly excludeReservationPriceCategory =
    (x: IShowPriceCategory) => !x.flagReservation;
  public readonly reservationsStore = inject(ReservationsStore);

  private readonly _showsStore = inject(ShowsStore);
  private readonly _cartStore = inject(CartStore);
  private readonly _cartService = inject(CartService);
  private readonly _kioskStore = inject(KioskStore);
  private readonly _sellingOverviewPriceCategoryGateService = inject(SellingOverviewPriceCategoryGateService);
  private readonly _priceCategoryGildepassService = inject(PriceCategoryGildepassService);

  private pendingPriceCategoryForAttach: IShowPriceCategory | null = null;
  private pendingAcardoPriceCategory: IShowPriceCategory | null = null;

  public get isAcardoSelectedCategory(): boolean {
    const selectedPriceCategory = this.priceCategories.find(category => category.id === this.selectedPriceCategoryId);

    return !!selectedPriceCategory && this._priceCategoryGildepassService.isAcardoCategory(selectedPriceCategory);
  }

  public get isSelectedCategoryCineville(): boolean {
    return this.priceCategories.some(
      p => p.id === this.selectedPriceCategoryId && p.flagCineville
    );
  }

  public get selectedCategoryRequiresSingleTicket(): boolean {
    const cat = this.selectedPriceCategory;

    if (!cat || !this._sellingOverviewPriceCategoryGateService.categoryUsesReductionCard(cat)) {
      return false;
    }

    return this._sellingOverviewPriceCategoryGateService.countCustomerCardsForCategory(cat) <= 1;
  }

  public get stepperMaxForApplyAll(): number {
    const cat = this.selectedPriceCategory;

    if (!cat || !this._sellingOverviewPriceCategoryGateService.categoryUsesReductionCard(cat)) {
      return this.showTickets.length;
    }

    const cardCount = this._sellingOverviewPriceCategoryGateService.countCustomerCardsForCategory(cat);
    return Math.min(this.showTickets.length, Math.max(cardCount, 0));
  }

  public onPriceCategoryChipClick(priceCat: IShowPriceCategory): void {
    if (this._priceCategoryGildepassService.isAcardoCategory(priceCat)) {
      this.enforceSingleTicketMode();
      this.pendingAcardoPriceCategory = priceCat;
      this.cardCodeInputModalComponent.open(null, 'submit-gildepass-code');
      return;
    }

    this._priceCategoryGildepassService.clearPendingMetadata();

    if (priceCat.flagCineville) {
      const showBegin = this.data?.ticket?.showBegin ?? this.show?.beginning;
      if (CinevilleUtils.isCinevilleCheckInTooEarly(showBegin)) {
        AlertModalComponent.openAlertModal(
          'Info'.trans('notifications/info.Title'),
          CinevilleUtils.getCinevilleCheckInTooEarlyMessage(),
          this.destroyRef
        );
        return;
      }

      this.enforceSingleTicketMode();
      this.selectedPriceCategoryId = +priceCat.id;
      this.cdr.markForCheck();
      return;
    }

    if (this.needsCardCodeModalForPriceCategory(priceCat)) {
      this.pendingPriceCategoryForAttach = priceCat;
      this.cardCodeInputModalComponent.open(null, 'add-payment-method');
      return;
    }

    if (!this._sellingOverviewPriceCategoryGateService.hasRequiredCardAttached(priceCat)) {
      const cardTypeName = this._sellingOverviewPriceCategoryGateService.getRequiredCardName(priceCat) ?? '';
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'Für diese Preiskategorie ist eine Karte des Typs [[cardTypeName]] erforderlich.'.trans(
          'cart/price-category-picker-modal/notifications/CardRequiredForPriceCategory.Message',
          [['cardTypeName', cardTypeName]]
        )
      );
      return;
    }

    this.selectedPriceCategoryId = +priceCat.id;

    if (
      this._sellingOverviewPriceCategoryGateService.categoryUsesReductionCard(priceCat)
      && this._sellingOverviewPriceCategoryGateService.countCustomerCardsForCategory(priceCat) <= 1
    ) {
      this.isApplyToAllEnabled = false;
      this.selectedTicketsCount = 0;
    } else if (this.isApplyToAllEnabled && this._sellingOverviewPriceCategoryGateService.categoryUsesReductionCard(priceCat)) {
      this.selectedTicketsCount = Math.min(this.selectedTicketsCount, this.stepperMaxForApplyAll);
    }

    this.cdr.markForCheck();
  }

  public onAttachCardModalClosed(): void {
    this.pendingPriceCategoryForAttach = null;
    this.pendingAcardoPriceCategory = null;
  }

  public onCardAttachedForPriceCategory(ev: { cardNr: string }): void {
    const priceCat = this.pendingPriceCategoryForAttach;
    if (!priceCat?.cardTypeId) {
      return;
    }

    if (this._sellingOverviewPriceCategoryGateService.isCardWrongTypeForCategory(ev.cardNr, priceCat)) {
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'This card cannot be used for this price category.'.trans(
          'cart/price-category-picker-modal/notifications/CardWrongTypeForPriceCategory.Message'
        )
      );
      return;
    }

    this.selectedPriceCategoryId = +priceCat.id;
    this._priceCategoryGildepassService.clearPendingMetadata();

    if (this._sellingOverviewPriceCategoryGateService.categoryUsesReductionCard(priceCat)) {
      this.isApplyToAllEnabled = false;
      this.selectedTicketsCount = 0;
    }

    this.cdr.markForCheck();
  }

  public onAcardoCodeSubmitted(ev: { code?: string }): void {
    const priceCat = this.pendingAcardoPriceCategory;

    if (!priceCat || !ev.code) {
      return;
    }

    this._priceCategoryGildepassService.clearPendingMetadata();

    this._priceCategoryGildepassService.validateAcardoVoucherForTicket(this.data.ticket, ev.code).subscribe({
      next: result => {
        if (result.status === 'invalid') {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            result.errorKey.trans(`vouchers/external/acardo/notifications/${result.errorKey}.Message`)
          );
          return;
        }

        if (result.status === 'error') {
          this.pendingAcardoPriceCategory = null;
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            result.errorMessage.trans(`add-voucher-item-to-cart/notifications/${result.errorMessage}.Message`)
          );
          return;
        }

        this.selectedPriceCategoryId = +priceCat.id;
        this.enforceSingleTicketMode();
        this.pendingAcardoPriceCategory = null;
        this.cdr.markForCheck();
      }
    });
  }

  public override open(val?: { ticket: ICartItem; }): void {
    this.selectedPriceCategoryId = val.ticket.priceCategoryId;
    this.initialSelectedPriceCategoryId = val.ticket.priceCategoryId;
    this.selectedTicketsCount = 0;
    this.isApplyToAllEnabled = false;
    this._priceCategoryGildepassService.clearPendingMetadata();
    this.priceCategories = this._cartStore.cart().showPriceCategories
      .find(p => p.showId === val.ticket.showId)
      .categories
      .map(c => {
        c.packages = c.packages
          .filter(
            p =>
              this._kioskStore.kioskProductsAndPackages().package.find(p2 => p2.id === p.id)
          );

        return c;
      });

    if (this.reservationsStore.rebookReservation()) {
      this.priceCategories = this.priceCategories.filter(c => c.flagReservation);
    }

    this.show = this._showsStore.shows().find(s => s.id === val.ticket.showId);
    this.showTickets = this._cartStore.cartItemsTickets().filter(t => t.showId === val.ticket.showId);

    if (this.selectedCategoryRequiresSingleTicket || this.isSelectedCategoryCineville) {
      this.isApplyToAllEnabled = false;
      this.selectedTicketsCount = 0;
    }

    super.open(val);
  }

  public onApplyToAllEnabledChange(enabled: boolean): void {
    if (this.isAcardoSelectedCategory || this.isSelectedCategoryCineville || this.selectedCategoryRequiresSingleTicket) {
      this.enforceSingleTicketMode();
      this.cdr.markForCheck();
      return;
    }

    this.selectedTicketsCount = enabled ? this.stepperMaxForApplyAll : 0;
    this.cdr.markForCheck();
  }

  public confirmPriceCategoriesSelection(btn: HTMLButtonElement): void {
    const selectedPriceCategory = this.priceCategories.find(p => p.id === this.selectedPriceCategoryId);

    if (!selectedPriceCategory) {
      return;
    }

    const isAcardoCategory = this._priceCategoryGildepassService.isAcardoCategory(selectedPriceCategory);
    const isCinevilleCategory = !!selectedPriceCategory.flagCineville;
    const shouldApplyAcardoMetadata = !this.isApplyToAllEnabled && isAcardoCategory;
    let ticketsToUpdate: ICartItem[];

    if (isAcardoCategory || isCinevilleCategory) {
      this.enforceSingleTicketMode();
      ticketsToUpdate = [{
        ...this.data.ticket,
        priceCategoryId: this.selectedPriceCategoryId,
        packageProductIds: [],
        packageId: null
      }];
    } else if (!this.selectedCategoryRequiresSingleTicket && this.isApplyToAllEnabled) {
      ticketsToUpdate = this.showTickets
        .slice(0, this.selectedTicketsCount)
        .map(t => ({
          ...t,
          priceCategoryId: this.selectedPriceCategoryId,
          packageProductIds: [],
          packageId: null
        }));

      if (this.selectedPriceCategoryId === this.priceCategories.find(p => p.flagReservation)?.id) {
        ticketsToUpdate = ticketsToUpdate.filter(t => !t.isFromReservation);
      }
    } else {
      ticketsToUpdate = [{
        ...this.data.ticket,
        priceCategoryId: this.selectedPriceCategoryId,
        packageProductIds: [],
        packageId: null
      }];
    }

    if (ticketsToUpdate.length === 0) {
      return;
    }

    const ticketsWithAcardoMetadata = this._priceCategoryGildepassService.enrichTicketsForPriceCategoryChange(
      ticketsToUpdate,
      selectedPriceCategory,
      shouldApplyAcardoMetadata
    );

    if (selectedPriceCategory.packages.length > 0) {
      const seatSelections = ticketsWithAcardoMetadata.map(t => ({
        rankToken: t.seatRow,
        seatNumber: t.seatNr,
        priceAreaId: t.priceAreaId,
        priceCategoryId: t.priceCategoryId,
        id: t.itemId
      }));

      const kioskProductsAndPackages = this._kioskStore.kioskProductsAndPackages();

      if (
        PackageUtils.canAutoSelectPackagesForAllSeats(
          seatSelections,
          selectedPriceCategory,
          kioskProductsAndPackages)
      ) {
        const seatPackageSelections = PackageUtils.getAutoSelectedPackages(
          seatSelections,
          selectedPriceCategory,
          kioskProductsAndPackages
        );

        const tickets: ICartItem[] = ticketsWithAcardoMetadata.map(t => {
          const seatData = seatPackageSelections.find(s => s.seat.id === t.itemId);

          return {
            ...t,
            packageId: seatData.package.id,
            packageProductIds: seatData.selectedProductIds
          };
        });

        this.finalizeCategorySelection(tickets, selectedPriceCategory, btn);

        return;
      }

      TicketPackageSelectionModalComponent.instance.open(
        seatSelections,
        selectedPriceCategory,
        seatSelectionsResult => {
          const tickets: ICartItem[] = ticketsWithAcardoMetadata.map(t => {
            const seatData = seatSelectionsResult.find(s => s.seat.id === t.itemId);

            return {
              ...t,
              packageId: seatData.package?.id,
              packageProductIds: seatData.selectedProductIds
            };
          });

          this.finalizeCategorySelection(tickets, selectedPriceCategory, btn);
        }
      );
    } else {
      this.finalizeCategorySelection(ticketsWithAcardoMetadata, selectedPriceCategory, btn);
    }
  }

  public onCinevilleCategoryChangeCancelled(): void {
    this.selectedPriceCategoryId = this.initialSelectedPriceCategoryId;
    this.cdr.markForCheck();
  }

  private finalizeCategorySelection(
    tickets: ICartItem[],
    priceCategory: IShowPriceCategory,
    btn: HTMLButtonElement
  ): void {
    if (priceCategory.flagCineville) {
      const pendingTicket = tickets[0];
      if (!pendingTicket) {
        return;
      }
      this._cinevillePartnerCodeModal.open({
        pendingTicket,
        revertPriceCategoryId: this.initialSelectedPriceCategoryId,
        targetCategoryName: priceCategory.name
      });
      return;
    }

    this.updateCartItemWithDetach(tickets, priceCategory, btn);
  }

  private updateCartItemWithDetach(
    ticketsToUpdate: ICartItem[],
    targetPriceCategory: IShowPriceCategory,
    btn: HTMLButtonElement
  ): void {
    const cart = this._cartStore.cart();
    const leavingCineville = ticketsToUpdate.filter(t => {
      const live = cart?.items?.find(i => i.itemId === t.itemId && i.position === t.position);
      return Boolean(live?.partnerCode) && targetPriceCategory.flagCineville !== true;
    });

    if (leavingCineville.length === 0) {
      this.updateCartItem(ticketsToUpdate, btn);
      return;
    }

    btn.classList.add('disabled');

    forkJoin(
      leavingCineville.map(t =>
        this._cartService.detachCinevillePartnerCode(t.itemId, t.position)
      )
    )
      .pipe(
        switchMap(() => this._cartService.updateCartItem(ticketsToUpdate)),
        finalize(() => btn.classList.remove('disabled'))
      )
      .subscribe({
        next: () => this.close(),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`update-ticket-price-category/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private get selectedPriceCategory(): IShowPriceCategory | undefined {
    return this.priceCategories.find(p => +p.id === +this.selectedPriceCategoryId);
  }

  private countTicketsNeedingCardForCategory(priceCat: IShowPriceCategory): number {
    const othersWithCategory = this.showTickets.filter(
      t => t.itemId !== this.data.ticket.itemId && +t.priceCategoryId === +priceCat.id
    ).length;

    return othersWithCategory + 1;
  }

  private needsCardCodeModalForPriceCategory(priceCat: IShowPriceCategory): boolean {
    return this._sellingOverviewPriceCategoryGateService.needsCardCodeModalForPriceCategory(
      priceCat,
      this.countTicketsNeedingCardForCategory(priceCat)
    );
  }

  private updateCartItem(ticketsToUpdate: ICartItem[], btn: HTMLButtonElement): void {
    btn.classList.add('disabled');

    this._cartService.updateCartItem(ticketsToUpdate)
      .pipe(
        finalize(() => btn.classList.remove('disabled'))
      )
      .subscribe({
        next: () => this.close(),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`update-ticket-price-category/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private enforceSingleTicketMode(): void {
    this.isApplyToAllEnabled = false;
    this.selectedTicketsCount = 0;
  }
}
